import { Component, computed, DestroyRef, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { FloatingPanelComponent } from './floating-panel.component';
import { WebRTCCallService, WebRTCCallState } from '../../core/services/webrtc-call.service';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { TalkPanelConfig } from '../../core/services/ai-talk-panel.service';
import { FloatingComponentData } from '../../core/services/floating-component.service';
import { DEFAULT_RING_COLOR, ImageColorExtractorService } from './image-color-extractor.service';

@Component({
  selector: 'ai-assistant-talk-panel',
  imports: [MatButtonModule, MatIconModule, GalaxyAvatarModule, FloatingPanelComponent, AiAssistantI18nModule],
  templateUrl: './ai-assistant-talk-panel.component.html',
  styleUrls: ['./ai-assistant-talk-panel.component.scss'],
})
export class AiAssistantTalkPanelComponent implements OnInit, FloatingComponentData {
  private static readonly AUTO_CLOSE_MS = 1500;

  config: TalkPanelConfig = {};

  setData(data: Record<string, unknown>): void {
    if (data['config']) {
      this.config = data['config'] as TalkPanelConfig;
    }
  }

  readonly floatingPanel = viewChild.required<FloatingPanelComponent>('floatingPanel');

  private readonly webrtcCallService = inject(WebRTCCallService);
  private readonly imageColorExtractor = inject(ImageColorExtractorService);
  private readonly destroyRef = inject(DestroyRef);
  private callStartTimeout: ReturnType<typeof setTimeout> | null = null;
  private autoCloseTimeout: ReturnType<typeof setTimeout> | null = null;
  private callInitiated = false;

  protected readonly isSpeaking = computed(() => this.webrtcCallService.isRemoteAudioActive());
  protected readonly isCallerSpeaking = computed(
    () => !this.webrtcCallService.isMuted() && this.webrtcCallService.isLocalAudioActive(),
  );
  protected readonly isConnecting = signal(false);
  protected readonly isConnected = signal(false);
  protected readonly isCallEnded = signal(false);
  protected readonly hasError = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly computedRingColor = signal(DEFAULT_RING_COLOR);
  protected readonly isMuted = this.webrtcCallService.isMuted;

  constructor() {
    effect(() => {
      const state = this.webrtcCallService.callState();
      this.updateStateBasedOnCall(state);
    });

    this.destroyRef.onDestroy(() => {
      if (this.callStartTimeout) {
        clearTimeout(this.callStartTimeout);
        this.callStartTimeout = null;
      }

      if (this.autoCloseTimeout) {
        clearTimeout(this.autoCloseTimeout);
        this.autoCloseTimeout = null;
      }

      if (this.webrtcCallService.callState() !== WebRTCCallState.IDLE) {
        this.webrtcCallService.endCall();
      }
    });
  }

  ngOnInit(): void {
    this.updateRingColor();
    this.isConnecting.set(true);

    this.callStartTimeout = setTimeout(() => {
      this.startCall();
    }, 500);
  }

  private updateStateBasedOnCall(state: WebRTCCallState): void {
    this.hasError.set(false);
    this.errorMessage.set('');

    switch (state) {
      case WebRTCCallState.IDLE:
        this.isConnecting.set(false);
        this.isConnected.set(false);
        break;

      case WebRTCCallState.CONNECTING:
        this.isConnecting.set(true);
        this.isConnected.set(false);
        this.isCallEnded.set(false);
        this.floatingPanel().autoClosing.set(false);
        if (this.autoCloseTimeout) {
          clearTimeout(this.autoCloseTimeout);
          this.autoCloseTimeout = null;
        }
        break;

      case WebRTCCallState.CONNECTED:
        this.isConnecting.set(false);
        this.isConnected.set(true);
        break;

      case WebRTCCallState.DISCONNECTED:
        this.isConnecting.set(false);
        this.isConnected.set(false);
        if (this.callInitiated && this.webrtcCallService.remoteHangup()) {
          this.isCallEnded.set(true);
          this.scheduleAutoClose();
        }
        break;

      case WebRTCCallState.ERROR:
        this.isConnecting.set(false);
        this.isConnected.set(false);
        this.hasError.set(true);
        this.errorMessage.set(this.webrtcCallService.error()?.message || 'An error occurred during the call');
        this.floatingPanel().autoClosing.set(false);
        if (this.autoCloseTimeout) {
          clearTimeout(this.autoCloseTimeout);
          this.autoCloseTimeout = null;
        }
        break;
    }
  }

  protected async startCall(): Promise<void> {
    try {
      this.callInitiated = true;
      this.hasError.set(false);
      this.errorMessage.set('');
      await this.webrtcCallService.startCall(this.config.assistantId ?? '');
    } catch (error) {
      console.error('Failed to start call:', error);
    }
  }

  protected async endCall(): Promise<void> {
    try {
      await this.webrtcCallService.endCall();
    } catch (error) {
      console.error('Failed to end call:', error);
    }
  }

  private scheduleAutoClose(): void {
    this.floatingPanel().autoClosing.set(true);
    this.autoCloseTimeout = setTimeout(() => {
      this.floatingPanel().close();
    }, AiAssistantTalkPanelComponent.AUTO_CLOSE_MS);
  }

  protected toggleMute(): void {
    this.webrtcCallService.toggleMute();
  }

  private updateRingColor(): void {
    if (this.config.ringColor) {
      this.computedRingColor.set(this.config.ringColor);
    } else if (this.config.assistantAvatarUrl) {
      this.imageColorExtractor.extractDominantColor(this.config.assistantAvatarUrl).then((color) => {
        this.computedRingColor.set(color);
      });
    }
  }

  close(): void {
    if (this.webrtcCallService.callState() !== WebRTCCallState.IDLE) {
      this.endCall();
    }

    this.floatingPanel().close();
  }
}
